import { useState, useRef, useEffect, useCallback } from 'react';
import { Search, X, AlertCircle } from 'lucide-react';
import api from '@/lib/axios';

export interface Cie11Result {
  id: string;          // URI de la entidad
  theCode: string;     // ej. "CA01.0"
  title: string;       // nombre del diagnóstico
}

interface Props {
  value?: Cie11Result | null;
  onChange: (v: Cie11Result | null) => void;
  placeholder?: string;
  disabled?: boolean;
}

function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);
  useEffect(() => {
    const t = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debouncedValue;
}

export function Cie11Search({ value, onChange, placeholder = 'Buscar diagnóstico CIE-11...', disabled }: Props) {
  const [query,   setQuery]   = useState('');
  const [results, setResults] = useState<Cie11Result[]>([]);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState(false);
  const [open,    setOpen]    = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const debouncedQuery = useDebounce(query, 350);

  const search = useCallback(async (q: string) => {
    if (q.trim().length < 3) { setResults([]); setOpen(false); return; }
    setLoading(true);
    setError(false);
    try {
      const json = await api.get('/api/catalogos/cie11/search', { params: { q } }).then(r => r.data);
      const entities: Cie11Result[] = (json.destinationEntities || []).map((e: any) => ({
        id:      e.id       || '',
        theCode: e.theCode  || '',
        title:   typeof e.title === 'string' ? e.title : (e.title?.['@value'] || e.title?.value || ''),
      })).filter((e: Cie11Result) => e.title);
      setResults(entities);
      setOpen(entities.length > 0);
    } catch {
      setError(true);
      setOpen(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { search(debouncedQuery); }, [debouncedQuery, search]);

  useEffect(() => {
    function handler(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  function select(r: Cie11Result) {
    onChange(r);
    setQuery('');
    setOpen(false);
    setResults([]);
  }

  function clear() {
    onChange(null);
    setQuery('');
  }

  // Mostrar el seleccionado como chip
  if (value) {
    return (
      <div className="flex items-center gap-2 flex-wrap">
        <div className="flex items-center gap-2 bg-primary-50 border border-primary-200 rounded-xl px-3 py-2 min-w-0">
          <span className="text-[11px] font-bold text-primary-700 bg-primary-100 px-2 py-0.5 rounded-md font-mono flex-shrink-0">
            {value.theCode}
          </span>
          <span className="text-sm text-primary-900 font-medium truncate">{value.title}</span>
          {!disabled && (
            <button type="button" onClick={clear}
              className="ml-1 p-0.5 rounded-full hover:bg-primary-200 text-primary-500 flex-shrink-0 transition-colors">
              <X size={13} />
            </button>
          )}
        </div>
        {!disabled && (
          <button type="button" onClick={clear}
            className="text-xs text-text-secondary hover:text-text-primary underline transition-colors">
            Cambiar
          </button>
        )}
      </div>
    );
  }

  return (
    <div ref={ref} className="relative">
      <div className="relative">
        {loading
          ? <div className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 border-2 border-primary-300 border-t-primary-600 rounded-full animate-spin" />
          : <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-secondary" />
        }
        <input
          value={query}
          onChange={e => setQuery(e.target.value)}
          onFocus={() => results.length > 0 && setOpen(true)}
          placeholder={placeholder}
          disabled={disabled}
          className="w-full border border-gray-200 rounded-lg pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300 bg-white disabled:opacity-50"
        />
        {query && (
          <button type="button" onClick={() => { setQuery(''); setResults([]); setOpen(false); }}
            className="absolute right-2.5 top-1/2 -translate-y-1/2 text-text-secondary hover:text-text-primary">
            <X size={13} />
          </button>
        )}
      </div>

      {query.length > 0 && query.length < 3 && (
        <p className="text-[11px] text-text-secondary mt-1 ml-1">Escribe al menos 3 caracteres para buscar</p>
      )}

      {error && (
        <div className="flex items-center gap-1.5 text-xs text-red-500 mt-1.5 ml-1">
          <AlertCircle size={12} /> Error al conectar con el servidor CIE-11
        </div>
      )}

      {open && results.length > 0 && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-100 rounded-xl shadow-xl z-50 max-h-64 overflow-y-auto">
          {results.map(r => (
            <button key={r.id} type="button" onClick={() => select(r)}
              className="flex items-start gap-3 w-full text-left px-3 py-2.5 hover:bg-primary-50 transition-colors border-b border-gray-50 last:border-0">
              <span className="text-[11px] font-bold text-primary-700 bg-primary-100 px-1.5 py-0.5 rounded font-mono flex-shrink-0 mt-0.5">
                {r.theCode}
              </span>
              <span className="text-sm text-text-primary leading-tight">{r.title}</span>
            </button>
          ))}
        </div>
      )}

      {open && results.length === 0 && !loading && query.length >= 3 && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-100 rounded-xl shadow-lg z-50 px-4 py-3">
          <p className="text-sm text-text-secondary text-center">Sin resultados para "{query}"</p>
        </div>
      )}
    </div>
  );
}
